import React, { useMemo, Suspense, lazy, useEffect } from "react";
import { CssBaseline, ThemeProvider, Box, CircularProgress } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { themeSettings } from "theme";
import Layout from "scenes/layout";
import Home from "scenes/home";
import Login from "scenes/login";
import RequireRole from "components/RequireRole";

const lazyPage = (loader) => lazy(loader);

const Dashboard = lazyPage(() => import("scenes/dashboard"));
const Tasks = lazyPage(() => import("scenes/tasks"));
const Members = lazyPage(() => import("scenes/customers"));
const Transactions = lazyPage(() => import("scenes/transactions"));
const Geography = lazyPage(() => import("scenes/geography"));
const Overview = lazyPage(() => import("scenes/overview"));
const Daily = lazyPage(() => import("scenes/daily"));
const Monthly = lazyPage(() => import("scenes/monthly"));
const Breakdown = lazyPage(() => import("scenes/breakdown"));
const Admin = lazyPage(() => import("scenes/admin"));
const Performance = lazyPage(() => import("scenes/performance"));
const Calendar = lazyPage(() => import("scenes/calendar"));
const Kanban = lazyPage(() => import("scenes/kanban"));
const Feedback = lazyPage(() => import("scenes/feedback"));
const Notifications = lazyPage(() => import("scenes/notifications"));
const Recharge = lazyPage(() => import("scenes/recharge"));
const Callers = lazyPage(() => import("scenes/callers"));
const CallerInterviews = lazyPage(() => import("scenes/callerInterviews"));
const InterviewList = lazyPage(() => import("scenes/interviewList"));
const InterviewSchedule = lazyPage(() => import("scenes/interviewSchedule"));
const Applications = lazyPage(() => import("scenes/applications"));
const MyDailyProgress = lazyPage(() => import("scenes/myProgress"));

const FinanceOverview = lazyPage(() => import("scenes/finance/FinanceOverview"));
const FinanceInvoices = lazyPage(() => import("scenes/finance/FinanceInvoices"));
const CallerMyInvoice = lazyPage(() => import("scenes/finance/CallerMyInvoice"));
const BidderTransactions = lazyPage(() => import("scenes/finance/BidderTransactions"));
const CashFlow = lazyPage(() => import("scenes/finance/CashFlow"));
const AccountManagement = lazyPage(() => import("scenes/finance/AccountManagement"));
const BusinessManagement = lazyPage(() => import("scenes/businessManagement"));
const Hiring = lazyPage(() => import("scenes/hiring"));
const ResumeOps = lazyPage(() => import("scenes/resumeOps"));

const PageFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
    <CircularProgress size={28} />
  </Box>
);

const Page = ({ children }) => <Suspense fallback={<PageFallback />}>{children}</Suspense>;

const App = () => {
  const mode = useSelector((state) => state.global.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode === "dark" ? "dark" : "light");
  }, [mode]);

  return (
    <div className="app">
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />

          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<Home />} />
            <Route path="/login" element={<Login />} />

            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Page><Dashboard /></Page>} />
              <Route path="/tasks" element={<Page><RequireRole allowedRoles={["admin", "superadmin", "caller"]}><Tasks /></RequireRole></Page>} />
              <Route path="/members" element={<Page><RequireRole allowedRoles={["admin", "superadmin", "lead"]}><Members /></RequireRole></Page>} />
              <Route path="/transactions" element={<Page><Transactions /></Page>} />
              <Route path="/geography" element={<Page><Geography /></Page>} />
              <Route path="/overview" element={<Page><Overview /></Page>} />
              <Route path="/daily" element={<Page><Daily /></Page>} />
              <Route path="/monthly" element={<Page><Monthly /></Page>} />
              <Route path="/breakdown" element={<Page><Breakdown /></Page>} />
              <Route path="/admin" element={<Page><RequireRole allowedRoles={["admin", "superadmin"]}><Admin /></RequireRole></Page>} />
              <Route path="/performance" element={<Page><Performance /></Page>} />
              <Route path="/calendar" element={<Page><Calendar /></Page>} />
              <Route path="/kanban" element={<Page><Kanban /></Page>} />
              <Route path="/feedback" element={<Page><Feedback /></Page>} />
              <Route path="/notifications" element={<Page><RequireRole allowedRoles={["admin", "superadmin", "lead", "caller", "member", "assistant"]}><Notifications /></RequireRole></Page>} />
              <Route path="/recharge" element={<Page><Recharge /></Page>} />
              {/* Profile Mapping retired — route kept only to redirect away if bookmarked */}
              <Route path="/profile-mapping" element={<Navigate to="/dashboard" replace />} />
              <Route path="/applications" element={<Page><RequireRole allowedRoles={["admin", "superadmin", "lead", "caller", "assistant"]}><Applications /></RequireRole></Page>} />
              <Route path="/callers" element={<Page><RequireRole allowedRoles={["admin", "superadmin"]}><Callers /></RequireRole></Page>} />
              <Route path="/interview-list" element={<Page><RequireRole allowedRoles={["admin", "superadmin", "caller", "assistant"]}><InterviewList /></RequireRole></Page>} />
              <Route path="/interview-schedule" element={<Page><RequireRole allowedRoles={["lead"]}><InterviewSchedule /></RequireRole></Page>} />
              <Route path="/interviews" element={<Page><RequireRole allowedRoles={["caller", "assistant"]}><CallerInterviews /></RequireRole></Page>} />
              <Route path="/finance/overview" element={<Page><RequireRole allowedRoles={["admin", "superadmin", "lead", "caller", "member"]}><FinanceOverview /></RequireRole></Page>} />
              <Route path="/finance/cash-flow" element={<Page><RequireRole allowedRoles={["admin", "superadmin"]}><CashFlow /></RequireRole></Page>} />
              <Route path="/finance/accounts" element={<Page><RequireRole allowedRoles={["admin", "superadmin"]}><AccountManagement /></RequireRole></Page>} />
              <Route path="/finance/invoices" element={<Page><RequireRole allowedRoles={["admin", "superadmin", "lead"]}><FinanceInvoices /></RequireRole></Page>} />
              <Route path="/finance/my-invoice" element={<Page><RequireRole allowedRoles={["caller"]}><CallerMyInvoice /></RequireRole></Page>} />
              <Route path="/finance/transactions" element={<Page><RequireRole allowedRoles={["member"]}><BidderTransactions /></RequireRole></Page>} />
              <Route path="/my-progress" element={<Page><RequireRole allowedRoles={["member"]}><MyDailyProgress /></RequireRole></Page>} />
              <Route path="/business/*" element={<Page><RequireRole allowedRoles={["admin", "superadmin"]}><BusinessManagement /></RequireRole></Page>} />
              <Route path="/hiring/*" element={<Page><RequireRole allowedRoles={["admin", "superadmin"]}><Hiring /></RequireRole></Page>} />
              <Route path="/resume-ops/*" element={<Page><RequireRole allowedRoles={["admin", "superadmin"]}><ResumeOps /></RequireRole></Page>} />
            </Route>
          </Routes>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
};

export default App;
